import { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Loader2, AlertCircle, Shield } from 'lucide-react'
import { resolveLoginRedirect } from '@/lib/redirect'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { mfaListFactors, mfaChallenge, mfaVerify } from '@/lib/supabase'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { captureEvent } from '@/lib/posthog'
import { BrandLockup } from '@/components/BrandLockup'

type Mode = 'totp' | 'recovery'

export function MfaChallengePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const isStepUp = searchParams.get('stepUp') === '1'
  const returnTo = searchParams.get('returnTo')
  const fromState = (location.state as { from?: { pathname: string; search?: string } } | null)?.from
  // `returnTo` is a query param, so treat it as attacker-controlled — same
  // /app-only allowlist the login redirect uses.
  const fallbackTarget = resolveLoginRedirect({ redirectParam: returnTo, fromState: fromState ?? null })

  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const aal = useAuthStore((s) => s.aal)
  const refreshAal = useAuthStore((s) => s.refreshAal)
  const signOut = useAuthStore((s) => s.signOut)

  const [mode, setMode] = useState<Mode>('totp')
  const [code, setCode] = useState('')
  const [recoveryCode, setRecoveryCode] = useState('')
  const [factorId, setFactorId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [helpOpen, setHelpOpen] = useState(false)
  const submittingRef = useRef(false)

  // R13 — self-gate. Without auth, redirect to login. If already aal2 and not
  // a step-up flow, return to destination.
  useEffect(() => {
    if (!isAuthenticated) {
      void navigate('/login', { replace: true })
      return
    }
    if (aal === 'aal2' && !isStepUp) {
      void navigate(fallbackTarget, { replace: true })
    }
  }, [isAuthenticated, aal, isStepUp, navigate, fallbackTarget])

  // Load factor ID once on mount. Read from data.all (full union of factor
  // statuses) rather than data.totp — Supabase's narrowed totp[] type claims
  // verified-only but at runtime can be empty for some accounts.
  useEffect(() => {
    void (async () => {
      try {
        const factors = await mfaListFactors()
        const totpFactors = (factors?.all ?? []).filter((f) => f.factor_type === 'totp')
        const verified = totpFactors.find((f) => f.status === 'verified')
        if (verified) {
          setFactorId(verified.id)
        } else {
          setError(
            'No verified authenticator found on your account. Use a recovery code, or contact your administrator to reset MFA.'
          )
        }
      } catch {
        setError('Could not load your authenticator. Try refreshing the page.')
      }
    })()
  }, [])

  const handleVerify = async () => {
    if (!factorId || code.length !== 6 || submittingRef.current) return
    submittingRef.current = true
    setIsLoading(true)
    setError(null)
    try {
      const challenge = await mfaChallenge(factorId)
      await mfaVerify(factorId, challenge.id, code)
      await refreshAal()
      sessionStorage.setItem('bc.mfa.lastAal2At', String(Date.now()))
      void navigate(fallbackTarget, { replace: true })
    } catch (e) {
      const msg = e instanceof Error ? e.message : ''
      if (/429|rate/i.test(msg)) {
        setError('Too many attempts. Try again in a few minutes.')
      } else if (/expired|timeout/i.test(msg)) {
        setError('Verification timed out. Please try again.')
      } else {
        setError("That code wasn't valid. Try again with the next code your app shows.")
      }
      setCode('')
    } finally {
      submittingRef.current = false
      setIsLoading(false)
    }
  }

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setCode(digits)
    if (digits.length === 6 && !isLoading) {
      void handleVerify()
    }
  }

  const handleRecoverySubmit = async () => {
    const trimmed = recoveryCode.trim()
    if (trimmed.length < 20) return
    setIsLoading(true)
    setError(null)
    try {
      await api.post('/users/me/mfa-recovery-verify', { code: trimmed })
      captureEvent('mfa_recovery_used')
      // Per spec §3.3 — sign the user out so they re-authenticate cleanly.
      await signOut()
      void navigate('/login?recoveryUsed=1', { replace: true })
    } catch (e) {
      const msg = e instanceof Error ? e.message : ''
      if (/429|too many/i.test(msg)) {
        setError('Too many recovery attempts. Try again in an hour.')
      } else {
        setError('That recovery code is not valid.')
      }
      setRecoveryCode('')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    void navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center hero-gradient p-4">
      <div className="w-full max-w-md">
        <div className="relative flex items-center justify-center mb-6">
          <BrandLockup iconSize={24} wordmarkClassName="text-2xl" />
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="absolute right-0 text-meta hover:underline"
          >
            Sign out
          </button>
        </div>

        <Card className="p-4">
          <CardHeader className="space-y-3">
            <div className="flex justify-center">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                <Shield className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            <CardTitle className="text-lg text-center">
              {isStepUp ? 'Confirm your identity to enter the admin panel' : 'Two-step verification'}
            </CardTitle>
            <CardDescription className="text-center">
              {mode === 'totp'
                ? 'Enter the verification code from your authenticator app'
                : 'Enter one of your recovery codes'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {mode === 'totp' ? (
              <div className="space-y-2">
                <Label htmlFor="challenge-code" className="text-meta">
                  Verification code
                </Label>
                <Input
                  id="challenge-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  className="text-center text-lg tracking-widest font-mono"
                  autoFocus
                />
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => void handleVerify()}
                  disabled={code.length !== 6 || isLoading || !factorId}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Verify
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="recovery-code" className="text-meta">
                  Recovery code
                </Label>
                <Input
                  id="recovery-code"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                  className="font-mono text-sm"
                  autoFocus
                />
                <Button
                  type="button"
                  className="w-full"
                  onClick={() => void handleRecoverySubmit()}
                  disabled={recoveryCode.trim().length < 20 || isLoading}
                >
                  {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Verify recovery code
                </Button>
              </div>
            )}

            <div className="flex flex-col gap-1 pt-2 text-meta">
              {mode === 'totp' ? (
                <button
                  type="button"
                  className="hover:underline self-start"
                  onClick={() => {
                    setMode('recovery')
                    setError(null)
                    setCode('')
                  }}
                >
                  Use a recovery code instead
                </button>
              ) : (
                <button
                  type="button"
                  className="hover:underline self-start"
                  onClick={() => {
                    setMode('totp')
                    setError(null)
                    setRecoveryCode('')
                  }}
                >
                  Use your authenticator app instead
                </button>
              )}
              <button
                type="button"
                className="hover:underline self-start"
                onClick={() => setHelpOpen(true)}
              >
                I've lost access to my authenticator
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Lost access to your authenticator?</DialogTitle>
            <DialogDescription>
              Contact your organisation administrator to reset MFA. For platform support,
              email <a className="underline" href="mailto:hello@bidcontext.ai">hello@bidcontext.ai</a>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button type="button" onClick={() => setHelpOpen(false)}>OK</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
